import { useMemo } from 'react';
import { useMeta, useUpdate } from '../../engine/api/hooks';
import { useWidgets } from '../../engine/api/context';
import { fieldOf } from '../../engine/meta/types';
import { Board } from '../../engine/views/Board';
import { EmptyState, Skeleton } from '../../engine/design/primitives/Misc';
import { formatDate, formatMinutes } from '../../lib/format';
import { useProjectDetail, type TaskRow } from './data';

// Board view: one engine kanban per phase (swimlanes), columns from the task status enum;
// dropping a card PATCHes status.

export function TasksBoard({ projectId, onOpen }: { projectId: string; onOpen: (id: string) => void }) {
  const detail = useProjectDetail(projectId);
  const meta = useMeta('project-tasks').data;
  const widgets = useWidgets();
  const update = useUpdate<TaskRow>('project-tasks', { toast: { success: 'Moved' }, onSuccess: detail.refresh });
  const statusField = meta ? fieldOf(meta, 'status') : undefined;

  const lanes = useMemo(() => {
    const d = detail.data;
    if (!d) return [];
    const out = d.phases.map((p) => ({ id: p.id, name: p.name, tasks: d.tasks.filter((t) => t.phase_id === p.id) }));
    const loose = d.tasks.filter((t) => !t.phase_id);
    if (loose.length || out.length === 0) out.push({ id: '', name: out.length ? 'No phase' : 'Tasks', tasks: loose });
    return out;
  }, [detail.data]);

  if (!meta || !statusField) return <Skeleton lines={4} />;
  if (detail.data && detail.data.tasks.length === 0) return <EmptyState title="No tasks yet" icon="kanban" hint="Add tasks to see them on the board." />;
  return (
    <div>
      {lanes.map((lane) => (
        <section key={lane.id || '_'} className="hf-prj-swimlane" aria-label={lane.name}>
          <div className="hf-caps">
            {lane.name} <span className="count">{lane.tasks.length}</span>
          </div>
          <Board<TaskRow>
            rows={lane.tasks}
            keyOf={(t) => t.id}
            field={statusField}
            labelOf={(t) => t.title}
            widgets={widgets}
            card={(t) => (
              <div className="meta">
                <span className="hf-mono">{t.ref}</span>
                {t.assignee_name && <span>{t.assignee_name}</span>}
                {t.due_date && <span className="hf-num">due {formatDate(t.due_date)}</span>}
                {(t.estimate_minutes > 0 || t.actual_minutes > 0) && (
                  <span className="hf-num">
                    {formatMinutes(t.actual_minutes)}
                    {t.estimate_minutes > 0 && <span className="hf-muted"> / {formatMinutes(t.estimate_minutes)}</span>}
                  </span>
                )}
              </div>
            )}
            onOpen={(t) => onOpen(t.id)}
            onMove={(t, value, previous) => update.mutate({ id: t.id, body: { status: value as TaskRow['status'] }, previous: { status: (previous ?? t.status) as TaskRow['status'] } })}
            empty={<div className="hf-muted" style={{ padding: 'var(--s-3) 0' }}>No tasks in this phase</div>}
          />
        </section>
      ))}
    </div>
  );
}
